import { fetchCurrentUser } from '../../api/me';
import { getApiToken } from '../../api/authToken';
import { upsertAppointment } from '../../api/appointments';
import { parseAppointmentDate } from '../../shared/calendar-sync/parseAppointmentDate';

async function getUserCalendarIntegrations() {
  const token = await getApiToken();
  const payload = await fetchCurrentUser(token);
  return payload?.user?.calendarIntegrations || {};
}

function ensureDate(value, label) {
  const date = value instanceof Date ? value : parseAppointmentDate(value);
  if (!date) {
    throw new Error(`Invalid ${label}`);
  }
  return date;
}

export const syncWithGoogleCalendar = async (appointment, userId, skipAuth = false) => {
  try {
    const integrations = await getUserCalendarIntegrations();
    if (!integrations.google) {
      throw new Error('Google Calendar integration is not enabled');
    }

    if (!window.gapi) {
      await new Promise((resolve) => {
        const script = document.createElement('script');
        script.src = 'https://apis.google.com/js/api.js';
        script.async = true;
        script.defer = true;
        script.onload = resolve;
        document.head.appendChild(script);
      });
    }

    if (!window.gapi.client) {
      await new Promise((resolve) => {
        window.gapi.load('client', resolve);
      });

      await window.gapi.client.init({
        apiKey: process.env.REACT_APP_GOOGLE_API_KEY,
        discoveryDocs: ['https://www.googleapis.com/discovery/v1/apis/calendar/v3/rest'],
      });
    }

    const token = localStorage.getItem('googleCalendarToken');
    const tokenExpiry = localStorage.getItem('googleCalendarTokenExpiry');
    const isTokenValid = token && tokenExpiry && new Date(tokenExpiry) > new Date();

    if (!isTokenValid) {
      await new Promise((resolve, reject) => {
        const tokenClient = window.google.accounts.oauth2.initTokenClient({
          client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
          scope: 'https://www.googleapis.com/auth/calendar https://www.googleapis.com/auth/calendar.events',
          callback: (response) => {
            if (response.error) {
              reject(new Error(response.error));
            } else if (response.access_token) {
              localStorage.setItem('googleCalendarToken', response.access_token);
              localStorage.setItem(
                'googleCalendarTokenExpiry',
                new Date(Date.now() + response.expires_in * 1000).toISOString()
              );
              window.gapi.client.setToken(response);
              resolve();
            } else {
              reject(new Error('No access token received'));
            }
          },
        });

        tokenClient.requestAccessToken({ prompt: skipAuth ? '' : 'consent' });
      });
    } else {
      window.gapi.client.setToken({ access_token: token });
    }

    const formatToRFC3339 = (date) => {
      const pad = (num) => (num < 10 ? '0' : '') + num;
      const year = date.getFullYear();
      const month = pad(date.getMonth() + 1);
      const day = pad(date.getDate());
      const hours = pad(date.getHours());
      const minutes = pad(date.getMinutes());
      const seconds = pad(date.getSeconds());
      const offset = -date.getTimezoneOffset();
      const offsetHours = pad(Math.floor(Math.abs(offset) / 60));
      const offsetMinutes = pad(Math.abs(offset) % 60);
      const offsetSign = offset >= 0 ? '+' : '-';

      return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}${offsetSign}${offsetHours}:${offsetMinutes}`;
    };

    const startDate = ensureDate(appointment.dateTime, 'start date');
    const endDate = ensureDate(appointment.endTime, 'end date');

    const event = {
      summary: `Appointment with ${appointment.clientName}`,
      description: appointment.notes || '',
      start: {
        dateTime: formatToRFC3339(startDate),
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
      end: {
        dateTime: formatToRFC3339(endDate),
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
      reminders: {
        useDefault: true,
      },
      transparency: 'opaque',
      visibility: 'private',
    };

    let response;
    if (appointment.isUpdate && appointment.googleEventId) {
      response = await window.gapi.client.calendar.events.patch({
        calendarId: 'primary',
        eventId: appointment.googleEventId,
        resource: event,
      });
    } else {
      response = await window.gapi.client.calendar.events.insert({
        calendarId: 'primary',
        resource: event,
      });
    }

    return {
      success: true,
      eventId: response.result.id,
      wasUpdated: appointment.isUpdate && appointment.googleEventId,
    };
  } catch (error) {
    return {
      success: false,
      error: error.message,
    };
  }
};

export const syncExistingAppointments = async (appointments, userId, companyId = null) => {
  try {
    const integrations = await getUserCalendarIntegrations();
    if (!integrations.google) {
      throw new Error('Google Calendar integration is not enabled');
    }

    const results = [];

    for (const appointment of appointments) {
      if (appointment.googleEventId) continue;

      const result = await syncWithGoogleCalendar(
        {
          ...appointment,
          dateTime: appointment.dateTime,
          endTime: appointment.endTime,
          clientName: appointment.clientName,
          isUpdate: false,
        },
        userId,
        false
      );

      if (result.success && result.eventId && companyId && appointment.id) {
        await upsertAppointment({
          companyId,
          id: appointment.id,
          appointment: { googleEventId: result.eventId },
        });
      }

      results.push({
        appointmentId: appointment.id,
        ...result,
      });
    }

    return {
      success: true,
      results,
    };
  } catch (error) {
    return {
      success: false,
      error: error.message,
    };
  }
};
